import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { PartnerContact } from '@/components/common/contact_components/PartnerContact';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { SelectedPartnerContext } from '@/Contexts/SelectedPartnerContext';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import useContact from '@/hooks/useContact';
import usePartner from '@/hooks/usePartner';
import { ContactProps, ContactType, localizedTextEquals } from '@/models/Contact';
import { ProgramNames } from '@/models/Program';
import { validateContact } from '@/utils/contactUtils';
import { TabSubPanelContent } from '@/components/myaccount/Applicant_MyAccount/TabSubPanelContent';

function AccountShippingContact({
  programId,
  onUnsavedChanges,
  onSave,
}: {
  programId: ProgramNames;
  onUnsavedChanges?: () => void;
  onSave?: () => void;
}) {
  const { partnerInfo, updatePartnerInfo } = useContext(SelectedPartnerContext);

  const { patchPartnerRecord } = usePartner();
  const { setAlert } = useContext(SnackbarContext);
  const { getContact, saveContact } = useContact();
  const { translatedText } = useContext(LocaleContext);
  const { contactTypes } = useContext(ConfigContext);
  const [missingContactFields, setMissingContactFields] = useState<Array<keyof ContactProps>>([]);

  const [shippingContact, setShippingContact] = useState<ContactProps | null>(null);

  const [savingContact, setSavingContact] = useState<boolean>(false);

  const shippingContactType = useMemo(
    () =>
      contactTypes.find(
        (c: ContactType) =>
          localizedTextEquals(c.contactTypeName, 'Shipping Address') && c.programId === programId
      ),
    [contactTypes, programId]
  );

  useEffect(() => {
    const fetchContact = async () => {
      if (partnerInfo?.shippingContactId != null) {
        const contact: ContactProps = await getContact(partnerInfo.shippingContactId);
        setShippingContact(contact);
      } else if (partnerInfo != null && partnerInfo.shippingContactId == null) {
        setShippingContact({});
      }
    };
    void fetchContact();
  }, [getContact, partnerInfo]);

  useEffect(() => {
    if (
      partnerInfo?.id != null &&
      shippingContact?.id != null &&
      shippingContact.id !== partnerInfo.shippingContactId
    ) {
      const updatePartner = async () => {
        if (partnerInfo?.id == null || shippingContact?.id == null) return;
        const updatedPartner = await patchPartnerRecord(partnerInfo.id, {
          shippingContactId: shippingContact.id,
        });
        updatePartnerInfo(updatedPartner);
      };

      void updatePartner();
    }
  }, [partnerInfo, patchPartnerRecord, shippingContact, updatePartnerInfo]);

  const handleContactChange = useCallback(
    (contact: ContactProps) => {
      setShippingContact(contact);
      onUnsavedChanges?.();
    },
    [onUnsavedChanges]
  );

  useEffect(() => {
    if (shippingContact == null || shippingContactType == null) return;

    const invalidFields = validateContact(shippingContact, shippingContactType);

    setMissingContactFields(invalidFields);
  }, [shippingContact, shippingContactType]);

  const handleContactSave = useCallback(async () => {
    if (shippingContact == null) return;

    if (missingContactFields.length > 0) {
      setAlert({
        message: translatedText.myaccount.errorHelperText,
        severity: 'error',
      });
      return;
    }

    setSavingContact(true);
    const contact: ContactProps | null = await saveContact(shippingContact);
    if (contact != null) {
      setShippingContact(contact);

      setAlert({
        message: translatedText.myaccount.shippingContactUpdated,
        severity: 'success',
      });
      onSave?.();
    } else {
      setAlert({
        message: translatedText.myaccount.errorUpdatingShippingContact,
        severity: 'error',
      });
    }

    setSavingContact(false);
  }, [missingContactFields, saveContact, setAlert, shippingContact, translatedText, onSave]);

  return (
    <TabSubPanelContent
      headerText={translatedText.signup.headerShipping}
      onSave={handleContactSave}
      saveDisabled={savingContact}
      isPanelLoading={shippingContact == null}
    >
      <PartnerContact
        id={`shipping-contact-${shippingContact?.id}`}
        contact={shippingContact!}
        contactType={shippingContactType}
        onChange={handleContactChange}
      />
    </TabSubPanelContent>
  );
}

export default AccountShippingContact;
